<template>
    <LoadingCard v-if="loading"></LoadingCard>
    <div class="card" v-else>
        <div class="bg-primary align-items-center d-flex">
            <h5 class="card-header text-white">Your Blocks</h5>
            <b-pagination align="right" class="my-auto mr-2 ml-auto" :total-rows="pagination.total" v-model="pagination.page"
                          :per-page="pagination.limit" />
        </div>
        <b-table hover stacked="md" :items="tableData" :fields="blocksTable.fields"
                 :per-page="pagination.limit" :current-page="pagination.page"
                 :show-empty="true">

            <template slot="cropType" slot-scope="row">
                <span v-if="row.item.cropType">{{ row.item.cropType.name }}</span>
                <span class="badge badge-danger" v-else>Removed</span>
            </template>

            <template slot="variety" slot-scope="row">
                <span v-if="row.item.cropType && row.item.variety">{{ row.item.variety.name }}</span>
                <span class="badge badge-danger" v-else-if="row.item.cropType">Removed</span>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="withholdingPeriods" slot-scope="row">
                <template v-if="row.item.currentWithholdingPeriods.length > 0">
                    <span class="badge-group mr-2" v-for="period in activeWithholdingPeriods(row.item)">
                        <span class="badge badge-secondary mb-1">{{ period.country.name }} ({{ period.length }} days)</span>
                        <span class="badge mb-1" :class="{'badge-success': period.is_past, 'badge-danger': !period.is_past}">{{ period.ends_at | date_format }}</span>
                    </span>
                </template>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="restrictedEntryInterval" slot-scope="row">
                <strong class="text-danger" v-if="row.item.restrictedEntryInterval">{{ row.item.restrictedEntryInterval.ends_at | date_format
                                                                                    }}</strong>
                <span class="text-muted" v-else>(none)</span>
            </template>

            <template slot="final_spray" slot-scope="row">
                <a href="#" class="badge badge-pill" :class="row.item.final_spray ? 'badge-success' : 'badge-light'"
                   @click.prevent="toggleFinalSpray(row.item)">
                    {{ row.item.final_spray ? 'Yes' : 'No' }}
                </a>
            </template>

            <template slot="actions" slot-scope="row">

                <b-button :to="{name: 'blocks.update', params: {blockId: row.item.id}}"
                          variant="secondary btn--icon"
                          v-b-tooltip.hover="'Edit Block'">
                    <i class="zmdi zmdi-edit"></i>
                </b-button>

                <!-- Spray Diary -->
                <b-button :to="{name: 'spray-diary.create', params: {blockId: row.item.id}}"
                          variant="orange btn--icon"
                          v-b-tooltip="'Update Spray Diary'">
                    <i class="zmdi zmdi-plus"></i>
                </b-button>
                <b-button :to="{name: 'spray-diary.view', params: {blockId: row.item.id}}"
                          variant="success btn--icon"
                          v-b-tooltip="'View Spray Diary'">
                    <i class="zmdi zmdi-view-list-alt"></i>
                </b-button>

            </template>
        </b-table>
    </div>

</template>

<script>
import updateFinalSprayMutation from '@views/Blocks/Browse/mutationUpdateFinalSpray.graphql';
import listBlocksQuery from '@views/Blocks/Browse/queryListBlocks.graphql';
import isAfter from 'date-fns/is_after';

export default {
  name: 'BrowseBlocks',
  props: ['cropTypeId'],
  components: {},
  apollo: {
    blocks: {
      query: listBlocksQuery,
      variables() {
        return {
          input: {
            crop_type_id: this.cropTypeId,
          },
        };
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.blocks = data.filterBlocks.data;
          this.pagination.total = data.filterBlocks.meta.total;
        }
      },
    },
  },
  data() {
    return {
      loading: 0,
      blocks: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultSmallPageSize,
        total: 0,
      },
      blocksTable: {
        fields: [
          { key: 'name', label: 'Block Name', sortable: true },
          { key: 'withholdingPeriods', label: 'Withholding Periods' },
          { key: 'restrictedEntryInterval', label: 'REI' },
          {
            key: 'cropType',
            label: 'Crop Type',
            sortable: true,
          },
          {
            key: 'variety',
            label: 'Crop Variety',
            sortable: true,
            formatter: variety => variety.name,
          },
          { key: 'final_spray', label: 'Final Spray' },
          { key: 'actions', label: '', class: 'text-right' },
        ],
      },
    };
  },
  methods: {
    toggleFinalSpray(block) {
      this.$apollo.mutate({
          mutation: updateFinalSprayMutation,
          variables: {
            input: {
              id: block.id,
              final_spray: !block.final_spray,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateBlock: {
              __typename: 'Block',
              id: block.id,
              final_spray: !block.final_spray,
            },
          },
        })
        .then(() => this.$notifySuccess('Changes saved!'))
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
    activeWithholdingPeriods(item) {
      const byCountry = {};
      item.currentWithholdingPeriods.forEach(period => {
        if (!(period.country.id in byCountry) || isAfter(period.ends_at, byCountry[period.country.id].ends_at)) {
          byCountry[period.country.id] = period;
        }
      });

      return Object.values(byCountry);
    },
  },
  computed: {
    tableData() {
      return (this.blocks || []).map(item => ({
        ...item,
        _cellVariants: { restrictedEntryInterval: item.restrictedEntryInterval ? 'danger' : null },
        // _rowVariant: item.restrictedEntryInterval ? 'danger' : null,
      }));
    },
  },
};
</script>
